import { watch } from 'vue';
import { useRoute } from 'vue-router';
import query from '@/components/query/query-constructor';
import filterService from '@/services/filter';
import { useStore } from '@/store';
import { useTableQuery } from '@/composables/useTableQuery';
import { useUrl } from '@/composables/useUrl';
import {
  DATETIME_QUERY_PARAMS,
  normalizeDateTimeQueryValue,
} from '@/views/reports/normalize-datetime-query-value';

const IGNORED_PARAMS = [
  'groupBy',
  'manual',
  'manualDrilldown',
  'picker',
  'search',
  'searchBy',
  'textLogicalRelation',
  'tagsLogicalRelation',
  'tags',
  'marks',
];
const COMMON_FILTER_PARAMS = ['currency', 'status', 'date'];

export const useUrlTableQuerySync = () => {
  const store = useStore();
  const route = useRoute();
  const tableQueryApi = useTableQuery();
  const { isDashboard, currentReportType } = useUrl();
  const isReport = () => !!route.params.report;

  const syncParam = (param: string, value: unknown) => {
    const normalizedValue = normalizeDateTimeQueryValue(param, value);
    tableQueryApi.patchTableQueryField({ name: param, value: normalizedValue });
    tableQueryApi.patchTableQueryTemporaryField({ name: param, value: normalizedValue });

    if (COMMON_FILTER_PARAMS.includes(param) && !isReport()) {
      store.patchDashboardQueryField({
        name: param,
        value: normalizedValue,
        notSetToLocalStorage: isReport(),
      });
    }
  };

  const syncFromUrl = () => {
    if (isDashboard.value) return;

    const pickURL = query.decode(route.params.data as string);
    if (!pickURL) {
      const tableQuery = tableQueryApi.tableQueryForCurrentTab.value;
      for (const param of DATETIME_QUERY_PARAMS) {
        if (param in tableQuery) {
          syncParam(param, tableQuery[param]);
        }
      }
      filterService.disarm();
      return;
    }

    for (const param of Object.keys(pickURL)) {
      if (param === 'offset') {
        const reportType = currentReportType.value;
        const groupBy = Array.isArray(reportType) ? reportType[0] : reportType;
        if (groupBy) {
          tableQueryApi.patchTableOffsetForCurrentTab({
            groupBy,
            value: Number(pickURL[param]) || 0,
          });
        }
        continue;
      }
      if (IGNORED_PARAMS.includes(param)) continue;
      syncParam(param, pickURL[param]);
    }

    filterService.disarm();
  };

  watch(
    () => route.fullPath,
    () => {
      syncFromUrl();
    },
    { immediate: true },
  );
};
